import React from "react";
import { useTranslation } from "react-i18next";
import { useGetSsoSettings } from "./hooks/sso-settings.hooks";
import { SsoProviderCard } from "./components/SsoProviderCard";
import { GoogleConfigDialog } from "./components/GoogleConfigDialog";
import { AppleConfigDialog } from "./components/AppleConfigDialog";
import { RemoveProviderDialog } from "./components/RemoveProviderDialog";
import { SsoProviderAlias } from "@/types/sso";

interface TenantSsoSettingsTabProps {
  tenantId: string;
  canWrite: boolean;
}

export const TenantSsoSettingsTab: React.FC<TenantSsoSettingsTabProps> = ({
  tenantId,
  canWrite,
}) => {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useGetSsoSettings(tenantId, true);

  const [googleOpen, setGoogleOpen] = React.useState(false);
  const [appleOpen, setAppleOpen] = React.useState(false);
  const [removeTarget, setRemoveTarget] =
    React.useState<SsoProviderAlias | null>(null);

  if (isLoading) {
    return (
      <div className="text-muted-foreground bg-background rounded-2xl border p-6 text-sm">
        {t("sso.page.loading")}
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="text-destructive bg-background rounded-2xl border p-6 text-sm">
        {t("sso.page.loadError")}
      </div>
    );
  }

  const google = data.providers.find((p) => p.alias === "google");
  const apple = data.providers.find((p) => p.alias === "apple");

  return (
    <div className="flex flex-col gap-6">
      <div className="bg-background flex flex-col gap-4 rounded-2xl border p-6">
        <p className="text-muted-foreground text-sm">
          {t("sso.page.description")}
        </p>
        <div className="flex flex-col gap-4">
          {google && (
            <SsoProviderCard
              provider={google}
              authorizedOrigin={data.authorizedOrigin}
              canWrite={canWrite}
              onConfigure={() => setGoogleOpen(true)}
              onRemove={() => setRemoveTarget("google")}
            />
          )}
          {apple && (
            <SsoProviderCard
              provider={apple}
              canWrite={canWrite}
              onConfigure={() => setAppleOpen(true)}
              onRemove={() => setRemoveTarget("apple")}
            />
          )}
        </div>
      </div>

      {canWrite ? (
        <>
          <GoogleConfigDialog
            open={googleOpen}
            onOpenChange={setGoogleOpen}
            tenantId={tenantId}
          />
          <AppleConfigDialog
            open={appleOpen}
            onOpenChange={setAppleOpen}
            tenantId={tenantId}
          />
        </>
      ) : null}

      {canWrite && removeTarget ? (
        <RemoveProviderDialog
          open={!!removeTarget}
          onOpenChange={(open) => {
            if (!open) setRemoveTarget(null);
          }}
          provider={removeTarget}
          providerLabel={t(`sso.providers.${removeTarget}`)}
          tenantId={tenantId}
        />
      ) : null}
    </div>
  );
};
